// useTranslation() — minimal i18n hook for F8M Content Studio.
// Reads/writes language to localStorage and notifies all subscribers via a custom event.
// Spec: docs/specs/FINAL-ADDITIONS.md ADDITION 3.

import { useEffect, useState, useCallback } from 'react';
import en from './en.json';
import pl from './pl.json';

const TRANSLATIONS = { en, pl };
const SUPPORTED = ['en', 'pl'];
const STORAGE_KEY = 'f8m-ui-language';
const CHANGE_EVENT = 'f8m-language-change';
const DEFAULT_LANGUAGE = 'en';

function getStoredLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

function lookup(dict, key) {
  return key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), dict);
}

function interpolate(template, vars) {
  if (typeof template !== 'string' || !vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{{${name}}}`,
  );
}

export function useTranslation() {
  const [language, setLanguageState] = useState(getStoredLanguage);

  useEffect(() => {
    function handleChange() {
      setLanguageState(getStoredLanguage());
    }
    window.addEventListener(CHANGE_EVENT, handleChange);
    window.addEventListener('storage', handleChange); // sync across tabs
    return () => {
      window.removeEventListener(CHANGE_EVENT, handleChange);
      window.removeEventListener('storage', handleChange);
    };
  }, []);

  const t = useCallback(
    (key, vars) => {
      const value =
        lookup(TRANSLATIONS[language], key) ??
        lookup(TRANSLATIONS[DEFAULT_LANGUAGE], key) ??
        key;
      return interpolate(value, vars);
    },
    [language],
  );

  const setLanguage = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore — storage may be blocked (private mode, etc.)
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { t, language, setLanguage };
}
